import getLogger from '@ignitesales/logger-es';
import ignite from '../../services/ignite';
import { validateQuestion, validateQuestionGroup } from '../../utils';
import {resetValidationErrors} from '../Validation/ValidationActions';
import {extractPageFromPath} from '../_utils/pageResponse/index';

const logger = getLogger({ module: 'question-action-creators' });

const QuestionActions = {
  ANSWER_VALUE_CHANGED: 'ANSWER_VALUE_CHANGED',
  VALIDATION_STATE_CHANGED: 'VALIDATION_STATE_CHANGED',
  VALIDATION_FOR_POSTAL_CODE_STARTED: 'VALIDATION_FOR_POSTAL_CODE_STARTED',
  VALIDATION_FOR_POSTAL_CODE_COMPLETE: 'VALIDATION_FOR_POSTAL_CODE_COMPLETE',
  CALCULATED_QUESTION_UPDATE_STARTED: 'CALCULATED_QUESTION_UPDATE_STARTED',
  CALCULATED_QUESTION_UPDATE_COMPLETED: 'CALCULATED_QUESTION_UPDATE_COMPLETED',
  CALCULATED_QUESTION_UPDATE_FAILED: 'CALCULATED_QUESTION_UPDATE_FAILED',
};

const answerChanged = ({
  question,
  questionGroup,
  value,
  calculatedQuestions = [],
}) => {
  return async (dispatch, getState) => {
    dispatch(answerChangedLocal({ question, questionGroup, value }));

    try {
      const { saveLocalOnly } = question.attributes || {};
      const { guideConfigurations: { isMockMode }, pages } = getState();
      calculatedQuestions.map((q) =>
        dispatch(updateCalculatedQuestionStarted(q))
      );
      if (
        ( !isMockMode && saveLocalOnly ) ||
        value == null ||
        (Array.isArray(value) && value.length === 0)
      ) {
        await ignite.answerService.removeAnswer(question.appkey);
      } else {
        await ignite.answerService.saveAnswer({
          appkey: question.appkey,
          value,
          timestamp: new Date(),
        });
      }
      calculatedQuestions.map((q) => dispatch(updateCalculatedQuestion(q)));

      //check for postal code change
      if( question.appkey === 'postalCode' && ignite?.pageResolutionService?.validateSurveyPage ) {
        dispatch({type: QuestionActions.VALIDATION_FOR_POSTAL_CODE_STARTED});
        const currentPage = extractPageFromPath(pages.currentPageUrl);
        const pageResponse = await ignite.pageResolutionService.validateSurveyPage({ page: currentPage });
        dispatch({type: QuestionActions.VALIDATION_FOR_POSTAL_CODE_COMPLETE,pageResponse});
      }

    } catch (ex) {
      logger.error('Error saving answer', ex);
      throw ex;
    }
  };
};

const answerChangedLocal = ({ question, questionGroup, value }) => {
  return async (dispatch, getState) => {
    const { saveLocalOnly } = question.attributes || {};

    const errors = validateQuestion(question, value);
    let groupErrors = null;

    const newAnswer = {
      appkey: question.appkey,
      timestamp: new Date(),
      value: value,
    };

    if (questionGroup) {
      const answers = {
        ...getState().answers,
        [question.appkey]: newAnswer,
      };
      groupErrors = validateQuestionGroup(questionGroup, answers);
    }

    if (saveLocalOnly) {
      const uuid = getState().user.uuid;
      const key = `${uuid}-localAnswers`;
      const localAnswers = JSON.parse(window.sessionStorage.getItem(key));
      window.sessionStorage.setItem(
        key,
        JSON.stringify({
          ...localAnswers,
          [question.appkey]: newAnswer,
        })
      );
    }

    dispatch(
      valueValidated(question, value, errors, questionGroup, groupErrors)
    );
    dispatch(valueChanged(question, value));
  };
};

/**
 * Common reducer to run when an answer is added / removed for a builders key options question.
 *
 * @param question
 * @param questionGroup
 * @param value
 * @param hasBeenRemoved
 * @param page
 * @returns {Function}
 */
const builderAnswerChanged = ({
  question,
  questionGroup,
  value,
  hasBeenRemoved,
  page = {},
}) => {
  return async (dispatch) => {
    dispatch(answerChanged({ question, value, questionGroup }));

    const calculatedQuestions = (page?.questionGroups || []).reduce((list, currentGroup) => {
      return list.concat(currentGroup?.questions?.filter(q => q.type.toLowerCase() === 'calculated'));
    }, []);
    calculatedQuestions.map(q => dispatch( updateCalculatedQuestionStarted(q) ));

    if (hasBeenRemoved) {
      question.answers.map((option) => {
        if (option.key === hasBeenRemoved) {
          (option.attributes.linkedQuestions || {}).map((linkedQuestionKey) => {
            dispatch(
              answerRemoved({
                question: questionGroup.questions.find( (q) => q.appkey === linkedQuestionKey )
              })
            );
          });
        }
      });
    }

    calculatedQuestions.map(q => dispatch( updateCalculatedQuestion(q) ));
    dispatch(resetValidationErrors());
    dispatch(builderGroupValidation({ question, questionGroup, value }));
  };
};

/**
 * Used to validate a builders linked questions. Since we don't want the server to assume that all questions in a builder is
 * required, we use client side validation. Any active options in the builder will automatically require the respective linked
 * questions to be required. Unless the "notRequired" attribute is present on a linked question.
 *
 * @param question
 * @param questionGroup
 * @param value
 * @returns {Function}
 */
const builderGroupValidation = ({ question, questionGroup, value }) => {
  return async (dispatch) => {
    question.answers.map((option) => {
      if (value.includes(option.key)) {
        (option.attributes.linkedQuestions || {}).map((linkedQuestionKey) => {
          const linkedQuestion = questionGroup.questions.find(
            (q) => q.appkey === linkedQuestionKey
          );
          dispatch(
            builderQuestionValidation({
              linkedQuestion,
              questionGroup,
              shouldDisplay: false,
            })
          );
        });
      }
    });
  };
};

const builderQuestionValidation = ({
  linkedQuestion,
  questionGroup,
  shouldDisplay,
  answerValue
}) => {
  return async (dispatch, getState) => {
    const answers = getState().answers;
    const attributes = linkedQuestion.attributes || {};
    const linkedQuestionAnswer = answers[linkedQuestion.appkey] || {};
    const valueToValidate = shouldDisplay ? answerValue : linkedQuestionAnswer.value != null ? linkedQuestionAnswer.value : answerValue;
    const isRequired = Object.prototype.hasOwnProperty.call(
      attributes,
      'required'
    )
      ? [{ type: 'required' }]
      : [];
    if (isRequired || valueToValidate) {
      const questionToValidate = {
        ...linkedQuestion,
        validations: [...isRequired, ...(linkedQuestion.validations || [])],
      };
      const errors = validateQuestion(questionToValidate, valueToValidate);
      dispatch(
        valueValidated(
          questionToValidate,
          valueToValidate,
          errors,
          questionGroup,
          null,
          shouldDisplay
        )
      );
    }
  };
};

const answerRemoved = ({ question }) => {
  return async (dispatch) => {
    dispatch( answerChangedLocal({question}) );

    try {
      await ignite.answerService.removeAnswer(question.appkey);
    } catch (ex) {
      logger.error('Error removing answer', ex);
      throw ex;
    }
  };
};

const updateCalculatedQuestion = (question) => {
  return async (dispatch) => {
    try {
      const response = await ignite.answerService.getAnswer(question.appkey);
      dispatch(updateCalculatedQuestionCompleted(question, response.value));
    } catch (ex) {
      dispatch(updateCalculatedQuestionFailed(ex));
    }
  };
};

const updateCalculatedQuestionCompleted = (question, value) => {
  return {
    type: QuestionActions.CALCULATED_QUESTION_UPDATE_COMPLETED,
    question,
    value,
  };
};

const updateCalculatedQuestionFailed = (error) => {
  return {
    type: QuestionActions.CALCULATED_QUESTION_UPDATE_FAILED,
    error,
  };
};

const updateCalculatedQuestionStarted = (question) => {
  return {
    type: QuestionActions.CALCULATED_QUESTION_UPDATE_STARTED,
    question,
  };
};

const valueChanged = (question, value) => ({
  type: QuestionActions.ANSWER_VALUE_CHANGED,
  local: true,
  question,
  value,
});

const valueValidated = (
  question,
  value,
  errors,
  questionGroup,
  groupErrors,
  shouldDisplay,
  resetErrors
) => ({
  type: QuestionActions.VALIDATION_STATE_CHANGED,
  local: true,
  errors,
  question,
  value,
  groupErrors,
  questionGroup,
  shouldDisplay,
  resetErrors
});

export {
  answerChanged,
  answerRemoved,
  builderAnswerChanged,
  builderGroupValidation,
  builderQuestionValidation,
  QuestionActions
};
